///
/// EarthBrowser
///
/// SynopticDatasetLayer - Synoptic weather visualization
///
/// Manages visualization of:
/// - Pressure centers (H/L markers) computed from EBWX grid
/// - Pressure field isolines (GPU shader) computed from EBWX grid
/// - Weather fronts from the 850 hPa theta-e grid ("fronts" sub-layer)
/// - The 250 hPa jet stream shell ("jetstream" sub-layer)
/// - 500 hPa height contours on their own shell ("upper_air" sub-layer)
///
/// All synoptic data is computed client-side from the WeatherGridService's
/// dedicated synoptic grids (-48h history / +48h forecast) rather than
/// downloaded from CDN Arrow datasets.
///
/// Created by Matt Giger on 1/18/2026
/// Copyright (c) 2026 Geodata Labs
///

import { FontCache } from "@/earthbrowser/utils/FontCache"
import { type Font } from "@/earthbrowser/libs/opentype.js"
import { geographic } from "@/earthbrowser/utils/geographic"
import { trig } from "@stovokor/math/math"

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { Database } from "@/earthbrowser/database/Database"
import { SynopticTable, type JetFieldBlobRow, type PressureCenterFeature, type PressureFieldBlobRow } from "./SynopticTable"
import { SynopticDataFrame } from "./SynopticDataFrame"
import { PressureCenterNode, type PressureTrackSummary } from "./PressureCenterNode"
import { FrontNode } from "./FrontNode"
import { cHeightContourStyle, PressureIsolineSphereNode } from "./PressureIsolineSphereNode"
import { cHeight500Shell, shellAltitudeKm } from "./shellAltitude"
import { PressureFieldComputedFrame } from "./PressureFieldComputedFrame"
import { JetStreamSphereNode } from "./JetStreamSphereNode"
import { JetStreamComputedFrame } from "./JetStreamComputedFrame"
import { PressureTrackNode, type PressureTrackPoint } from "./PressureTrackNode"
import { type JetMaximum } from "../weather-grid/JetStreamFieldGenerator"
import { type AxisFeature } from "../weather-grid/TroughRidgeDetector"
import { TroughRidgeNode } from "./TroughRidgeNode"
import { DatasetSeries } from "@/earthbrowser/datasets/DatasetSeries"
import { pickLoadState, type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { time, timedelta, timerange } from "@stovokor/math/time"
import type { RenderState } from "@stovokor/engine/RenderState"
import type { RenderActionSet } from "@stovokor/engine/Renderer"
// Import the concrete module, not the ../weather-grid barrel: the barrel
// re-exports React hooks, which would drag React into the embed bundle
import { getWeatherGridService } from "../weather-grid/WeatherGridService"
import { type EBWXHeader } from "../weather-grid/EBWXDecoder"
import { type IsobarLabel } from "./IsobarLabelExtractor"
import SynopticComputeWorker from "@/workers/synoptic_compute/index.ts?worker"
import { proxy, transfer, wrap, type Remote } from "comlink"
import type { SynopticComputeWorkerType } from "@/workers/synoptic_compute/index"
import { isMemoryConstrainedDevice } from "@/earthbrowser/utils/MemoryLog"
import { recordCrumb } from "@/earthbrowser/utils/CrashSentinel"
import { IsobarLabelNode } from "./IsobarLabelNode"
import { rgba } from "@stovokor/math/color"
import { HurricanesTable, parseTrack, type StormTrackPoint } from "../hurricanes/HurricanesTable"
import { combinedTimeline, stormStateAtTime } from "../hurricanes/stormTrack"

export type SynopticFeature = PressureCenterFeature

// A tropical cyclone is itself a low, so the grid detector always finds an L
// on top of the storm's spinning marker. Hide lows this close to a displayed
// storm; the radius covers the marker footprint (~124 km) plus the typical
// offset between the grid's detected minimum and the advisory center.
const cStormLowSuppressRadiusKm = 300

// Lows need this many correlated frames (3-hourly) before a track is drawn:
// one-frame detections are noise, and highs drift too slowly to be worth a line
const cMinTrackFrames = 3

// 500 hPa contour labels in the gold of their contour family
const cHeightLabelColor = rgba(1.0, 0.9, 0.65, 1)
// Jet streak labels in the pale cyan-white of the streaks
const cJetLabelColor = rgba(0.9, 0.97, 1.0, 1)

export class SynopticDatasetLayer extends NodeBase {
	/** Live instance for the dev debug hooks (window.__ebDebug.getSynoptic) */
	private static live?: SynopticDatasetLayer

	/** Displayed frame summary: centres with tracks and tendencies, fronts, jet */
	public static debugSnapshot(): Record<string, unknown> | null {
		const layer = SynopticDatasetLayer.live
		if (!layer) return null
		const frame = layer.lastRenderedFrame
		return {
			visible: layer.visible,
			frameTime: frame?.validTime ?? null,
			frames: layer.allFrames().length,
			centers: [...layer.pressureCenterNodes.values()].map(node => ({
				type: node.feature.center_type,
				lon: node.feature.lon,
				lat: node.feature.lat,
				pressure: node.feature.pressure_hpa,
				trackId: node.feature.track_id,
				tendency3h: node.lastTendency,
				tracked: layer.trackNodes.has(node.feature.track_id),
				visible: node.visible,
				callout: node.getInfoPanelData(),
				parts: node.debugParts(),
			})),
			tracks: layer.trackNodes.size,
			fronts: frame?.fronts.length ?? 0,
			frontTypes: frame ? frame.fronts.reduce<Record<string, number>>((acc, f) => { acc[f.type] = (acc[f.type] ?? 0) + 1; return acc }, {}) : {},
			jetMaxSpeedMps: layer.jetStreamMaxSpeedMps,
			jetMaxima: frame?.jetMaxima ?? [],
			jetLabels: layer.jetLabelNodes.map(n => ({ visible: n.visible, altitudeKm: layer.jetStreamSphere.altitudeKm })),
			heightLabels: frame?.heightLabels.length ?? 0,
			heightAxes: frame ? frame.heightAxes.map(a => ({ type: a.type, n: a.points.length, km: Math.round(a.lengthKm), lon: a.points[0].lon, lat: a.points[0].lat })) : [],
		}
	}

	private context: EarthBrowserContext
	private pressureCenterNodes = new Map<string, PressureCenterNode>()
	private pressureCenterContainer = new NodeBase()
	private trackNodes = new Map<string, PressureTrackNode>()
	private trackContainer = new NodeBase()
	private frontNodes = new Map<string, FrontNode>()
	private frontContainer = new NodeBase()
	private unsubscribe?: () => void
	private useGpuIsobars: boolean = true
	private showPressureTint: boolean = false
	// Sub-layer toggles ("fronts" / "jetstream" in visibleDatasets), gated
	// by the synoptic parent
	private frontsVisible = true
	private jetStreamVisible = true
	private upperAirVisible = false

	private synopticSeries = new DatasetSeries<SynopticDataFrame>()
	private lastRenderedFrame?: SynopticDataFrame

	private pressureIsolineSphere: PressureIsolineSphereNode
	private pressureFrameCache = new Map<string, PressureFieldComputedFrame>()
	private jetStreamSphere: JetStreamSphereNode
	private jetFrameCache = new Map<string, JetStreamComputedFrame>()
	private heightIsolineSphere: PressureIsolineSphereNode
	private heightFrameCache = new Map<string, PressureFieldComputedFrame>()
	private heightLabelContainer = new NodeBase()
	private heightLabelNodes: IsobarLabelNode[] = []
	private axisContainer = new NodeBase()
	private axisNodes: TroughRidgeNode[] = []
	private jetLabelContainer = new NodeBase()
	private jetLabelNodes: IsobarLabelNode[] = []
	private isobarLabelContainer = new NodeBase()
	private isobarLabelNodes: IsobarLabelNode[] = []
	private font?: Font

	private lastGridRefreshTimestamp = 0
	private gridRefreshInFlight = false
	private computeGeneration = 0
	private computeWorker?: Worker
	private computeWorkerRef?: Remote<SynopticComputeWorkerType>
	private lastComputedGridTimestamp = 0
	public visible = false

	// Active storm timelines for suppressing duplicate L markers
	private hurricanesVisible = false
	private stormTimelines: StormTrackPoint[][] = []
	private lastStormRefreshTimestamp = 0

	constructor(context: EarthBrowserContext) {
		super()
		this.context = context
		SynopticDatasetLayer.live = this

		this.synopticSeries.frequency = timedelta.hours(3)
		this.synopticSeries.endOffset = timedelta.hours(3)

		this.pressureIsolineSphere = new PressureIsolineSphereNode(context, { resolution: 2 })
		this.jetStreamSphere = new JetStreamSphereNode(context, 2)
		this.heightIsolineSphere = new PressureIsolineSphereNode(context, {
			resolution: 2,
			style: cHeightContourStyle,
			altitudeKm: cHeight500Shell.maxKm,
			altitudeForCamera: alt => shellAltitudeKm(cHeight500Shell, alt),
		})

		try {
			this.showPressureTint = globalThis.localStorage?.getItem("earthbrowser_synoptic_pressure_tint") === "true"
		} catch { }
		this.pressureIsolineSphere.setPressureTint(this.showPressureTint)

		this.addNode(this.trackContainer)
		this.addNode(this.pressureCenterContainer)
		this.addNode(this.frontContainer)
		this.addNode(this.pressureIsolineSphere)
		this.addNode(this.isobarLabelContainer)
		this.addNode(this.heightIsolineSphere)
		this.addNode(this.heightLabelContainer)
		this.addNode(this.axisContainer)
		this.addNode(this.jetStreamSphere)
		this.addNode(this.jetLabelContainer)

		this.subscribeToRedux()
	}

	/** Time span of loaded pressure-field frames (for the timeline scrubber) */
	public getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		const range = this.synopticSeries.timeRange
		return range ? { startMs: range.start.date.getTime(), endMs: range.end.date.getTime() } : null
	}

	/** Frame times and loaded spans for the timeline lane (see DatasetLayerManager.registerTimeRangeProvider) */
	public getTimeLaneDetail(): TimeLaneDetail {
		return {
			frameTimesMs: this.synopticSeries.frameTimesMs(),
			loadedRanges: this.synopticSeries.loadedRangesMs(),
		}
	}

	public setUseGpuIsobars(value: boolean): void {
		this.useGpuIsobars = value
		try {
			globalThis.localStorage?.setItem("earthbrowser_synoptic_use_gpu_isobars", value ? "true" : "false")
		} catch { }

		const state = engineBridge.getState()?.earthbrowser
		if (!state) return
		this.applyVisibility(state.visibleDatasets.includes("synoptic"))

		const currentFrame = this.synopticSeries.currentFrame
		if (currentFrame?.loaded && this.font) {
			this.displayFrame(currentFrame)
			this.lastRenderedFrame = currentFrame
		}
	}

	/**
	 * Toggles the pressure tint fill (cool blue lows, warm orange highs) on the
	 * isoline sphere. Persisted to localStorage so a future settings UI can hook
	 * into this directly.
	 */
	public setShowPressureTint(value: boolean): void {
		this.showPressureTint = value
		try {
			globalThis.localStorage?.setItem("earthbrowser_synoptic_pressure_tint", value ? "true" : "false")
		} catch { }
		this.pressureIsolineSphere.setPressureTint(value)
	}

	private subscribeToRedux(): void {
		const apply = (visibleDatasets: readonly string[]) => {
			this.frontsVisible = visibleDatasets.includes("fronts")
			this.jetStreamVisible = visibleDatasets.includes("jetstream")
			this.upperAirVisible = visibleDatasets.includes("upper_air")
			this.hurricanesVisible = visibleDatasets.includes("hurricane")
			this.applyVisibility(visibleDatasets.includes("synoptic"))
		}

		apply(engineBridge.getState()?.earthbrowser.visibleDatasets ?? [])
		this.unsubscribe = engineBridge.subscribe(() => {
			apply(engineBridge.getState()?.earthbrowser.visibleDatasets ?? [])
		})
	}

	private applyVisibility(masterVisible: boolean): void {
		const wasVisible = this.visible
		const jetWasVisible = this.jetStreamSphere.visible
		const upperWasVisible = this.heightIsolineSphere.visible
		this.visible = masterVisible
		this.pressureIsolineSphere.visible = masterVisible && this.useGpuIsobars
		this.isobarLabelContainer.visible = masterVisible && this.useGpuIsobars
		this.pressureCenterContainer.visible = masterVisible
		this.trackContainer.visible = masterVisible
		this.frontContainer.visible = masterVisible && this.frontsVisible
		this.jetStreamSphere.visible = masterVisible && this.jetStreamVisible
		this.jetLabelContainer.visible = masterVisible && this.jetStreamVisible
		this.heightIsolineSphere.visible = masterVisible && this.upperAirVisible
		this.heightLabelContainer.visible = masterVisible && this.upperAirVisible
		this.axisContainer.visible = masterVisible && this.upperAirVisible
		if (wasVisible && !masterVisible) {
			// Reclaim GPU field/flow texture memory while hidden; frames
			// re-compute from the persisted quantized fields when re-enabled
			this.pressureIsolineSphere.purgeTextures()
		}
		if (jetWasVisible && !this.jetStreamSphere.visible) {
			this.jetStreamSphere.purgeTextures()
		}
		if (upperWasVisible && !this.heightIsolineSphere.visible) {
			this.heightIsolineSphere.purgeTextures()
		}
	}

	/** Peak 250 hPa wind (m/s) in the displayed jet frame, 0 when hidden or unloaded */
	public get jetStreamMaxSpeedMps(): number {
		return this.jetStreamSphere.visible ? this.jetStreamSphere.currentMaxSpeedMps : 0
	}

	override render(state: RenderState): RenderActionSet[] {
		this.checkGridRefresh()
		this.checkStormRefresh()

		const clockTime = this.context.clock.clockTime
		this.synopticSeries.update(clockTime)

		let currentFrame = this.synopticSeries.currentFrame

		if (!currentFrame && this.synopticSeries.range.start.julian > 0) {
			currentFrame = this.findClosestFrame(clockTime)
			if (currentFrame) {
				this.synopticSeries.currentFrame = currentFrame
			}
		}

		if (currentFrame && currentFrame !== this.lastRenderedFrame) {
			if (currentFrame.loaded && this.font) {
				this.displayFrame(currentFrame)
				this.lastRenderedFrame = currentFrame
			} else if (!currentFrame.loading) {
				void currentFrame.load()
			}
		}

		if (currentFrame?.loaded && this.font && this.pressureCenterContainer.visible) {
			this.interpolatePressureCenters(clockTime)
			this.updateCenterScreenSpace(state)
		}

		if (currentFrame?.loaded && this.frontContainer.visible && this.frontNodes.size > 0) {
			this.interpolateFronts()
			this.updateFrontScreenSpace(state)
		}

		if (this.isobarLabelContainer.visible && this.isobarLabelNodes.length > 0) {
			this.updateLabelScreenSpace(state, this.isobarLabelNodes)
		}

		if (this.jetLabelContainer.visible && this.jetLabelNodes.length > 0) {
			const altitude = this.jetStreamSphere.altitudeKm
			for (const node of this.jetLabelNodes) node.setAltitude(altitude)
			this.updateLabelScreenSpace(state, this.jetLabelNodes)
		}

		if (this.heightLabelContainer.visible && this.heightLabelNodes.length > 0) {
			// Height labels ride the camera-dependent 500 hPa shell
			const altitude = this.heightIsolineSphere.altitudeKm
			for (const node of this.heightLabelNodes) node.setAltitude(altitude)
			this.updateLabelScreenSpace(state, this.heightLabelNodes)
		}

		if (this.axisContainer.visible && this.axisNodes.length > 0) {
			const altitude = this.heightIsolineSphere.altitudeKm
			for (const node of this.axisNodes) node.setAltitude(altitude)
		}

		return super.render(state)
	}

	/** Front glyph size and spacing follow the camera distance */
	private updateFrontScreenSpace(state: RenderState): void {
		const cameraPos = state.camera.position
		const fovDeg = (state.camera.projection as { fov?: number }).fov ?? 45
		const worldPerPixelAtUnitDist = 2 * Math.tan(fovDeg * 0.5 * trig.rad) / state.height
		for (const node of this.frontNodes.values()) {
			node.updateScreenSpace(cameraPos, worldPerPixelAtUnitDist)
		}
	}

	/** Keeps the H/L markers at a constant on-screen size while close */
	private updateCenterScreenSpace(state: RenderState): void {
		const cameraPos = state.camera.position
		const fovDeg = (state.camera.projection as { fov?: number }).fov ?? 45
		const worldPerPixelAtUnitDist = 2 * Math.tan(fovDeg * 0.5 * trig.rad) / state.height
		for (const node of this.pressureCenterNodes.values()) {
			node.updateScreenSpace(cameraPos, worldPerPixelAtUnitDist)
		}
	}

	/** Keeps contour labels at a constant on-screen size, fading them out at close zoom */
	private updateLabelScreenSpace(state: RenderState, nodes: IsobarLabelNode[]): void {
		const cameraPos = state.camera.position
		const altitudeKm = new geographic(cameraPos).altitude

		// fade in between 400 and 900 km altitude
		const fade = Math.min(1, Math.max(0, (altitudeKm - 400) / 500))

		const fovDeg = (state.camera.projection as { fov?: number }).fov ?? 45
		const worldPerPixelAtUnitDist = 2 * Math.tan(fovDeg * 0.5 * trig.rad) / state.height

		for (const node of nodes) {
			node.updateScreenSpace(cameraPos, worldPerPixelAtUnitDist, fade)
		}
	}

	/** Refresh the active storm timelines from the local hurricanes table on
	 *  the same cadence advisories are polled. Tolerates the hurricanes
	 *  dataset never having synced (missing table = no suppression). */
	private checkStormRefresh(): void {
		const now = Date.now()
		if (now - this.lastStormRefreshTimestamp < 5 * 60_000) return
		this.lastStormRefreshTimestamp = now

		HurricanesTable.getAll()
			.then(storms => {
				this.stormTimelines = storms.map(s =>
					combinedTimeline(parseTrack(s.observed_track), parseTrack(s.forecast_track))
				)
			})
			.catch(() => {
				this.stormTimelines = []
			})
	}

	private checkGridRefresh(): void {
		const now = Date.now()
		if (now - this.lastGridRefreshTimestamp < 5 * 60_000) return
		if (this.gridRefreshInFlight) return

		this.gridRefreshInFlight = true
		this.loadAndComputeSynoptic()
			.then(() => {
				this.lastGridRefreshTimestamp = Date.now()
			})
			.catch(err => {
				console.error("[SynopticDatasetLayer] Grid computation failed:", err)
			})
			.finally(() => {
				this.gridRefreshInFlight = false
				this.gridLoadState = null
			})
	}

	/**
	 * Why the synoptic picture is incomplete: fetching the weather grids,
	 * computing centres/isobars/fronts from them, or waiting on the frame
	 * for the clock's time. Null when the picture matches the clock. Read
	 * through DatasetLayerManager.registerLoadStateProvider
	 */
	public getLoadState(): LayerLoadState | null {
		return pickLoadState([this.gridLoadState, this.synopticSeries.loadState()])
	}

	private gridLoadState: LayerLoadState | null = null

	private async loadAndComputeSynoptic(): Promise<void> {
		const service = getWeatherGridService()
		// The fronts and jet grids are optional (those sub-layers just don't
		// render without them)
		this.gridLoadState = { phase: "downloading", since: performance.now() }
		await Promise.all([
			service.ensureSynopticGrid(),
			service.ensureFrontsGrid(),
			service.ensureJetGrid(),
			service.ensureUpperGrid(),
		])

		const header = service.synopticGridHeader
		const buffer = service.synopticGridBuffer
		if (!header || !buffer) return

		// The service only refetches every 30 minutes; skip the expensive
		// recompute when the grid data hasn't changed since the last one
		const gridTimestamp = service.synopticGridLoadedTimestamp
		if (gridTimestamp === this.lastComputedGridTimestamp) return

		this.gridLoadState = { phase: "ingesting", detail: "Computing", since: performance.now() }

		const frontsHeader = service.frontsGridHeader
		const frontsBuffer = service.frontsGridBuffer
		const jetHeader = service.jetGridHeader
		const jetBuffer = service.jetGridBuffer
		const upperHeader = service.upperGridHeader
		const upperBuffer = service.upperGridBuffer

		// The decompressed grids (~63MB + ~16MB + ~33MB) are handed to the
		// worker by transfer inside computeSynopticFromGrid, which detaches
		// them on this thread - so drop the service's references now. The
		// load timestamps survive, so the grids still count as fresh and
		// aren't refetched. (Trade-off: if the compute itself fails, synoptic
		// data waits for the next scheduled refetch instead of retrying from
		// the retained buffer. On the crashing devices, retaining a
		// main-thread copy of the grids for the whole compute is exactly the
		// duplication that kills the tab.)
		service.releaseSynopticBuffer()
		service.releaseFrontsBuffer()
		service.releaseJetBuffer()
		service.releaseUpperBuffer()

		await this.computeSynopticFromGrid(header, buffer, frontsHeader, frontsBuffer, jetHeader, jetBuffer, upperHeader, upperBuffer)
		this.lastComputedGridTimestamp = gridTimestamp
	}

	private getComputeWorker(): Remote<SynopticComputeWorkerType> {
		if (!this.computeWorkerRef) {
			this.computeWorker = new SynopticComputeWorker()
			this.computeWorkerRef = wrap<SynopticComputeWorkerType>(this.computeWorker)
		}
		return this.computeWorkerRef
	}

	/** Terminate the compute worker so its heap (cloned grid buffer plus
	 *  intermediate arrays) is released between the infrequent computes */
	private releaseComputeWorker(): void {
		this.computeWorker?.terminate()
		this.computeWorker = undefined
		this.computeWorkerRef = undefined
	}

	private async computeSynopticFromGrid(
		header: EBWXHeader,
		buffer: ArrayBuffer,
		frontsHeader: EBWXHeader | null = null,
		frontsBuffer: ArrayBuffer | null = null,
		jetHeader: EBWXHeader | null = null,
		jetBuffer: ArrayBuffer | null = null,
		upperHeader: EBWXHeader | null = null,
		upperBuffer: ArrayBuffer | null = null
	): Promise<void> {
		const generation = ++this.computeGeneration

		// Half-resolution field textures on memory-constrained devices: cuts
		// the persisted field blobs and GPU textures by 4x with little
		// visual impact since the field is blurred and bilinearly sampled
		const fieldDownsample = isMemoryConstrainedDevice() ? 2 : 1

		// Main-thread bracket log: worker console output is invisible in the
		// in-app log view, so mark the start of the compute window here
		console.log(`[SynopticDatasetLayer] Starting grid compute (field downsample ${fieldDownsample}x)`)
		recordCrumb(`synoptic:compute-start ds=${fieldDownsample}`)

		// The heavy per-timestep work (plane decode, center detection, field
		// generation, label extraction, optical flow) runs in a worker so the
		// render loop never blocks. Load the font concurrently. The proxied
		// progress callback drops crash breadcrumbs (the worker's own heap
		// counts toward the tab's memory limit but leaves no trail of its own).
		// The grid buffers move by transfer (zero-copy, detaching this
		// thread's view) instead of structured clone, which would double
		// ~80MB of grid data for the duration of the compute.
		const progress = proxy((msg: string) => recordCrumb(`synoptic:worker ${msg}`))
		let font: Font
		let results: Awaited<ReturnType<Remote<SynopticComputeWorkerType>["compute"]>>
		try {
			[font, results] = await Promise.all([
				this.font ?? FontCache.load("/fonts/Inter-Bold.otf"),
				this.getComputeWorker().compute(
					transfer(buffer, [buffer]),
					header,
					fieldDownsample,
					frontsBuffer ? transfer(frontsBuffer, [frontsBuffer]) : null,
					frontsHeader,
					progress,
					jetBuffer ? transfer(jetBuffer, [jetBuffer]) : null,
					jetHeader,
					upperBuffer ? transfer(upperBuffer, [upperBuffer]) : null,
					upperHeader
				),
			])
		} finally {
			this.releaseComputeWorker()
		}
		this.font = font
		recordCrumb(`synoptic:worker-done frames=${results.length}`)

		// A newer compute superseded this one while we were off-thread
		if (generation !== this.computeGeneration) return

		const frames: SynopticDataFrame[] = []
		const pressureFrames: PressureFieldComputedFrame[] = []
		const fieldRows: PressureFieldBlobRow[] = []
		// Frame per fieldRows entry (pressure and height frames interleave)
		const fieldFrames: PressureFieldComputedFrame[] = []
		const jetFrames: JetStreamComputedFrame[] = []
		const jetRows: JetFieldBlobRow[] = []
		const heightFrames: PressureFieldComputedFrame[] = []

		for (let t = 0; t < results.length; t++) {
			const result = results[t]
			const validDate = new Date(result.validTime * 1000)
			const validTimeISO = validDate.toISOString()

			const centers: PressureCenterFeature[] = result.centers.map((d, idx) => ({
				id: `local-${t}-${idx}`,
				valid_time: validTimeISO,
				center_type: d.type,
				lon: d.lon,
				lat: d.lat,
				pressure_hpa: d.pressure,
				track_id: '',
				track_seq: 0,
			}))

			const startTime = new time(validTimeISO)
			let endTime: time
			if (t < results.length - 1) {
				const nextValidTime = results[t + 1].validTime
				endTime = new time(new Date(nextValidTime * 1000).toISOString())
			} else {
				endTime = startTime.adding(timedelta.hours(3))
			}
			const range = new timerange(startTime, endTime)

			const frame = new SynopticDataFrame(validTimeISO, range, `local-${generation}`)
			frame.pressureCenters = centers
			frame.isobarLabels = result.labels
			frame.heightLabels = result.height500Labels
			frame.heightAxes = result.height500Axes
			frame.jetMaxima = result.jet?.maxima ?? []
			frame.fronts = result.fronts
			frame.loaded = true
			frames.push(frame)

			if (result.field) {
				const duration = t < results.length - 1
					? endTime.difference(startTime)
					: timedelta.hours(3)
				const frameId = `pressure-field-${generation}-${t}`
				const computedFrame = new PressureFieldComputedFrame(
					this.context,
					result.field.data,
					result.field.width,
					result.field.height,
					startTime,
					duration,
					frameId
				)
				if (result.flow) {
					computedFrame.setFlow(result.flow.data, result.flow.width, result.flow.height)
				}
				pressureFrames.push(computedFrame)
				fieldFrames.push(computedFrame)

				const fieldBytes = result.field.data
				const flowBytes = result.flow?.data
				fieldRows.push({
					id: frameId,
					valid_time: validTimeISO,
					width: result.field.width,
					height: result.field.height,
					field: new Uint8Array(fieldBytes.buffer, fieldBytes.byteOffset, fieldBytes.byteLength),
					flow: flowBytes ? new Uint8Array(flowBytes.buffer, flowBytes.byteOffset, flowBytes.byteLength) : null,
					flow_width: result.flow?.width ?? 0,
					flow_height: result.flow?.height ?? 0,
				})
			}

			if (result.height500) {
				// Same frame class and blob table as the pressure fields; the
				// id prefix keeps the two apart and clearPressureFields covers both
				const duration = t < results.length - 1
					? endTime.difference(startTime)
					: timedelta.hours(3)
				const frameId = `height500-field-${generation}-${t}`
				const heightFrame = new PressureFieldComputedFrame(
					this.context,
					result.height500.data,
					result.height500.width,
					result.height500.height,
					startTime,
					duration,
					frameId
				)
				heightFrames.push(heightFrame)
				fieldFrames.push(heightFrame)
				const bytes = result.height500.data
				fieldRows.push({
					id: frameId,
					valid_time: validTimeISO,
					width: result.height500.width,
					height: result.height500.height,
					field: new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength),
					flow: null,
					flow_width: 0,
					flow_height: 0,
				})
			}

			if (result.jet) {
				const duration = t < results.length - 1
					? endTime.difference(startTime)
					: timedelta.hours(3)
				const frameId = `jet-field-${generation}-${t}`
				jetFrames.push(new JetStreamComputedFrame(
					this.context,
					result.jet.data,
					result.jet.width,
					result.jet.height,
					result.jet.maxSpeedMps,
					startTime,
					duration,
					frameId
				))
				jetRows.push({
					id: frameId,
					valid_time: validTimeISO,
					width: result.jet.width,
					height: result.jet.height,
					max_speed_mps: result.jet.maxSpeedMps,
					field: result.jet.data,
				})
			}
		}

		// Correlate pressure center and front tracks across frames
		this.correlatePressureCenters(frames)
		this.correlateFronts(frames)

		// Replace synoptic series — setFrames purges old frames not in the new set
		this.lastRenderedFrame = undefined
		this.synopticSeries.setFrames(frames)

		// Update pressure field textures
		for (const [, frame] of this.pressureFrameCache) {
			frame.purge()
		}
		this.pressureFrameCache.clear()
		for (const pf of pressureFrames) {
			this.pressureFrameCache.set(pf.url, pf)
		}
		this.pressureIsolineSphere.setFrames(pressureFrames)

		for (const frame of this.heightFrameCache.values()) {
			frame.purge()
		}
		this.heightFrameCache.clear()
		for (const hf of heightFrames) {
			this.heightFrameCache.set(hf.url, hf)
		}
		this.heightIsolineSphere.setFrames(heightFrames)

		for (const frame of this.jetFrameCache.values()) {
			frame.purge()
		}
		this.jetFrameCache.clear()
		for (const jf of jetFrames) {
			this.jetFrameCache.set(jf.url, jf)
		}
		this.jetStreamSphere.setFrames(jetFrames)
		recordCrumb(`synoptic:frames-set n=${frames.length} jet=${jetFrames.length}`)

		// Persist the quantized fields so purged GPU frames can re-fetch from
		// sqlite instead of retaining CPU copies in heap
		void this.persistPressureFields(generation, fieldRows, fieldFrames)
			.then(() => this.persistJetFields(generation, jetRows, jetFrames))

		console.log(`[SynopticDatasetLayer] Computed ${frames.length} synoptic frames (${jetFrames.length} jet) from grid`)
	}

	/** Same scheme as persistPressureFields, run after it so the two blob
	 *  streams don't compete on the database queue */
	private async persistJetFields(
		generation: number,
		rows: JetFieldBlobRow[],
		jetFrames: JetStreamComputedFrame[]
	): Promise<void> {
		const db = Database.getDb()
		if (!db || rows.length === 0) return

		try {
			await SynopticTable.create(db)
			await SynopticTable.clearJetFields()
			for (let i = 0; i < rows.length; i++) {
				if (generation !== this.computeGeneration) return
				await SynopticTable.insertJetField(rows[i])
				jetFrames[i].markPersisted()
			}
			recordCrumb("synoptic:jet-persist-done")
		} catch (err) {
			console.warn("[SynopticDatasetLayer] Jet field persistence failed:", err)
		}
	}

	/** Writes one blob row per queue item at veryLow priority so interactive
	 *  database queries interleave between the ~2MB inserts. Each frame drops
	 *  its CPU field copy as soon as its row is queryable; on failure the
	 *  frame just keeps its copy and never faults to the database. */
	private async persistPressureFields(
		generation: number,
		rows: PressureFieldBlobRow[],
		pressureFrames: PressureFieldComputedFrame[]
	): Promise<void> {
		const db = Database.getDb()
		if (!db || rows.length === 0) return

		try {
			recordCrumb(`synoptic:persist-start rows=${rows.length}`)
			await SynopticTable.create(db)
			await SynopticTable.clearPressureFields()
			for (let i = 0; i < rows.length; i++) {
				// A newer compute superseded this set; its persist pass will
				// clear the table and write its own rows
				if (generation !== this.computeGeneration) return
				await SynopticTable.insertPressureField(rows[i])
				pressureFrames[i].markPersisted()
			}
			recordCrumb("synoptic:persist-done")
		} catch (err) {
			console.warn("[SynopticDatasetLayer] Pressure field persistence failed:", err)
		}
	}

	private correlatePressureCenters(frames: SynopticDataFrame[]): void {
		if (frames.length === 0) return

		let lowCounter = 0
		let highCounter = 0
		let lowTracks: TrackState[] = []
		let highTracks: TrackState[] = []

		for (const frame of frames) {
			const lows = frame.pressureCenters.filter(c => c.center_type === 'L')
			const highs = frame.pressureCenters.filter(c => c.center_type === 'H')

			const frameTime = new Date(frame.validTime).getTime()
			lowTracks = this.assignTracks('L', lows, lowTracks, () => `L-${++lowCounter}`, frameTime)
			highTracks = this.assignTracks('H', highs, highTracks, () => `H-${++highCounter}`, frameTime)
		}
	}

	private assignTracks(
		_type: string,
		detections: PressureCenterFeature[],
		tracks: TrackState[],
		nextId: () => string,
		frameTime: number
	): TrackState[] {
		if (tracks.length === 0) {
			return detections.map(d => {
				const id = nextId()
				d.track_id = id
				d.track_seq = 0
				return { id, lon: d.lon, lat: d.lat, pressure: d.pressure_hpa, seq: 0, lastTime: frameTime }
			})
		}

		const dtHours = Math.max(0.001, (frameTime - tracks[0].lastTime) / 3600000)
		const maxDistKm = 500 * dtHours + 200
		const maxPressureDelta = 35

		type Candidate = { ti: number, di: number, cost: number }
		const candidates: Candidate[] = []

		for (let ti = 0; ti < tracks.length; ti++) {
			const t = tracks[ti]
			for (let di = 0; di < detections.length; di++) {
				const d = detections[di]
				const dp = Math.abs(d.pressure_hpa - t.pressure)
				if (dp > maxPressureDelta) continue
				const dist = haversineKm(t.lon, t.lat, d.lon, d.lat)
				if (dist > maxDistKm) continue
				candidates.push({ ti, di, cost: dist + dp * 40 })
			}
		}

		candidates.sort((a, b) => a.cost - b.cost)
		const usedT = new Set<number>()
		const usedD = new Set<number>()
		const nextTracks: TrackState[] = []

		for (const c of candidates) {
			if (usedT.has(c.ti) || usedD.has(c.di)) continue
			usedT.add(c.ti)
			usedD.add(c.di)
			const t = tracks[c.ti]
			const d = detections[c.di]
			const seq = t.seq + 1
			d.track_id = t.id
			d.track_seq = seq
			nextTracks.push({ ...t, lon: d.lon, lat: d.lat, pressure: d.pressure_hpa, seq, lastTime: frameTime })
		}

		for (let di = 0; di < detections.length; di++) {
			if (usedD.has(di)) continue
			const d = detections[di]
			const id = nextId()
			d.track_id = id
			d.track_seq = 0
			nextTracks.push({ id, lon: d.lon, lat: d.lat, pressure: d.pressure_hpa, seq: 0, lastTime: frameTime })
		}

		return nextTracks
	}

	private interpolatePressureCenters(clockTime: time): void {
		const currentFrame = this.synopticSeries.currentFrame
		const nextFrame = this.synopticSeries.nextFrame
		if (!currentFrame?.loaded) return

		const stormPositions = this.stormPositionsAt(clockTime)

		let t = 0
		if (nextFrame?.loaded) {
			const frameDiff = nextFrame.range.start.difference(currentFrame.range.start)
			if (frameDiff.jdelta > 0) {
				const sceneDiff = clockTime.difference(currentFrame.range.start)
				t = Math.max(0, Math.min(1, sceneDiff.jdelta / frameDiff.jdelta))
			}
		}

		// Pressure one frame back per track, for the 3 h tendency. New tracks
		// (no history) fall back to the change toward the next frame
		const prevFrame = this.frameBefore(currentFrame)
		const prevByTrack = new Map<string, PressureCenterFeature>()
		for (const c of prevFrame?.pressureCenters ?? []) {
			prevByTrack.set(c.track_id || c.id, c)
		}
		const prevHours = prevFrame ? currentFrame.range.start.difference(prevFrame.range.start).secondsDelta / 3600 : 0

		if (t <= 0 || !nextFrame?.loaded) {
			for (const [key, node] of this.pressureCenterNodes) {
				const f = node.feature
				node.updateInterpolated(f.lon, f.lat, f.pressure_hpa)
				node.updateTendency(tendency3h(prevByTrack.get(key)?.pressure_hpa, f.pressure_hpa, prevHours))
				this.applyStormSuppression(node, f.lon, f.lat, stormPositions)
			}
			return
		}

		const frameHours = nextFrame.range.start.difference(currentFrame.range.start).secondsDelta / 3600
		const nextByTrack = new Map<string, PressureCenterFeature>()
		for (const c of nextFrame.pressureCenters) {
			const key = c.track_id || c.id
			nextByTrack.set(key, c)
		}

		for (const [key, node] of this.pressureCenterNodes) {
			const a = node.feature
			const b = nextByTrack.get(key)
			const prev = prevByTrack.get(key)
			if (!b) {
				node.updateInterpolated(a.lon, a.lat, a.pressure_hpa)
				node.updateTendency(tendency3h(prev?.pressure_hpa, a.pressure_hpa, prevHours))
				this.applyStormSuppression(node, a.lon, a.lat, stormPositions)
				continue
			}

			const lon = lerpLongitudeDeg(a.lon, b.lon, t)
			const lat = a.lat + (b.lat - a.lat) * t
			const pressure = a.pressure_hpa + (b.pressure_hpa - a.pressure_hpa) * t
			node.updateInterpolated(lon, lat, pressure)
			// Blend the tendency across the frame too so it doesn't step
			const tendency = prev
				? tendency3h(prev.pressure_hpa, a.pressure_hpa, prevHours) * (1 - t) + tendency3h(a.pressure_hpa, b.pressure_hpa, frameHours) * t
				: tendency3h(a.pressure_hpa, b.pressure_hpa, frameHours)
			node.updateTendency(tendency)
			this.applyStormSuppression(node, lon, lat, stormPositions)
		}
	}

	/** The loaded frame immediately before the given one, if any */
	private frameBefore(frame: SynopticDataFrame): SynopticDataFrame | undefined {
		const frames = this.allFrames()
		const index = frames.indexOf(frame)
		return index > 0 ? frames[index - 1] : undefined
	}

	/** All frames in time order (DatasetSeries keeps the list private) */
	private allFrames(): SynopticDataFrame[] {
		const series = this.synopticSeries as unknown as { frames?: SynopticDataFrame[] }
		return series.frames ?? []
	}

	/**
	 * Rebuild the low-pressure tracks for the displayed frame: every
	 * correlated position before it as history, every one after it as
	 * forecast. Highs get no track (see cMinTrackFrames).
	 */
	private updateTracks(frame: SynopticDataFrame): void {
		for (const node of this.trackNodes.values()) {
			this.trackContainer.removeNode(node)
			node.destroy()
		}
		this.trackNodes.clear()

		const frames = this.allFrames()
		const frameIndex = frames.indexOf(frame)
		if (frameIndex < 0) return

		for (const center of frame.pressureCenters) {
			if (!center.track_id) continue

			const past: PressureTrackPoint[] = []
			const future: PressureTrackPoint[] = []
			for (let i = 0; i < frames.length; i++) {
				const match = frames[i].pressureCenters.find(c => c.track_id === center.track_id)
				if (!match) continue
				const point: PressureTrackPoint = {
					lon: match.lon,
					lat: match.lat,
					pressureHpa: match.pressure_hpa,
					timeMs: new Date(frames[i].validTime).getTime(),
				}
				if (i <= frameIndex) past.push(point)
				else future.push(point)
			}

			// Motion (over the surrounding frames) and the track's extreme
			// central pressure feed the hover callout for highs and lows alike
			const centerNode = this.pressureCenterNodes.get(center.track_id)
			if (centerNode) {
				centerNode.setTrackSummary(trackSummary(center.center_type, past, future))
			}

			// Only lows get a drawn track (highs drift too slowly to be worth a line)
			if (center.center_type !== 'L') continue
			if (past.length + future.length < cMinTrackFrames) continue
			const node = new PressureTrackNode(this.context, center.center_type, past, future)
			this.trackNodes.set(center.track_id, node)
			this.trackContainer.addNode(node)
		}
	}

	/** Positions of active storms at the clock time. Empty when the
	 *  hurricanes overlay is hidden, so the L markers come back. */
	private stormPositionsAt(clockTime: time): { lat: number, lon: number }[] {
		if (!this.hurricanesVisible || this.stormTimelines.length === 0) return []

		const tMs = clockTime.date.getTime()
		const positions: { lat: number, lon: number }[] = []
		for (const timeline of this.stormTimelines) {
			const state = stormStateAtTime(timeline, tMs)
			if (state) {
				positions.push({ lat: state.lat, lon: state.lon })
			}
		}
		return positions
	}

	/** Hide a low marker that duplicates a displayed tropical cyclone */
	private applyStormSuppression(
		node: PressureCenterNode,
		lon: number,
		lat: number,
		storms: { lat: number, lon: number }[]
	): void {
		let suppressed = false
		if (node.feature.center_type === 'L') {
			for (const storm of storms) {
				if (haversineKm(lon, lat, storm.lon, storm.lat) < cStormLowSuppressRadiusKm) {
					suppressed = true
					break
				}
			}
		}
		node.visible = !suppressed
		// The low's track would duplicate the storm's own track
		const track = this.trackNodes.get(node.feature.track_id)
		if (track) track.visible = !suppressed
	}

	private findClosestFrame(clockTime: time): SynopticDataFrame | undefined {
		const frames = this.allFrames()
		if (frames.length === 0) return undefined

		if (clockTime.less(this.synopticSeries.range.start)) return frames[0]
		if (clockTime.greater(this.synopticSeries.range.end)) return frames[frames.length - 1]

		for (const frame of frames) {
			if (frame.range.contains(clockTime)) return frame
		}
		return frames[0]
	}

	private displayFrame(frame: SynopticDataFrame): void {
		if (!this.font) return
		this.updatePressureCenters(frame.pressureCenters)
		this.updateTracks(frame)
		this.updateIsobarLabels(frame.isobarLabels)
		this.updateHeightLabels(frame.heightLabels)
		this.updateHeightAxes(frame.heightAxes)
		this.updateJetLabels(frame.jetMaxima)
		this.updateFronts(frame.fronts)
	}

	private updateHeightAxes(axes: AxisFeature[]): void {
		for (const node of this.axisNodes) {
			this.axisContainer.removeNode(node)
			node.destroy()
		}
		this.axisNodes = []

		const altitude = this.heightIsolineSphere.altitudeKm
		for (const axis of axes) {
			const node = new TroughRidgeNode(this.context, axis, altitude)
			this.axisNodes.push(node)
			this.axisContainer.addNode(node)
		}
	}

	/** Jet streak cores labelled in knots on the jet shell */
	private updateJetLabels(maxima: JetMaximum[]): void {
		for (const node of this.jetLabelNodes) {
			this.jetLabelContainer.removeNode(node)
			node.destroy()
		}
		this.jetLabelNodes = []

		const altitude = this.jetStreamSphere.altitudeKm
		for (const max of maxima) {
			const knots = Math.round(max.speedMps * 1.943844 / 5) * 5
			const label: IsobarLabel = { lon: max.lon, lat: max.lat, pressureHpa: max.speedMps }
			const node = new IsobarLabelNode(this.context, label, this.font!, altitude, cJetLabelColor, `${knots} kt`)
			this.jetLabelNodes.push(node)
			this.jetLabelContainer.addNode(node)
		}
	}

	private updateHeightLabels(labels: IsobarLabel[]): void {
		for (const node of this.heightLabelNodes) {
			this.heightLabelContainer.removeNode(node)
			node.destroy()
		}
		this.heightLabelNodes = []

		const altitude = this.heightIsolineSphere.altitudeKm
		for (const label of labels) {
			const node = new IsobarLabelNode(this.context, label, this.font!, altitude, cHeightLabelColor)
			this.heightLabelNodes.push(node)
			this.heightLabelContainer.addNode(node)
		}
	}

	private updateFronts(fronts: SynopticDataFrame["fronts"]): void {
		for (const node of this.frontNodes.values()) {
			this.frontContainer.removeNode(node)
			node.destroy()
		}
		this.frontNodes.clear()

		for (let i = 0; i < fronts.length; i++) {
			const front = fronts[i]
			const key = front.trackId || `front-${i}`
			const node = new FrontNode(this.context, front)
			this.frontNodes.set(key, node)
			this.frontContainer.addNode(node)
		}
	}

	/**
	 * Match fronts across consecutive frames by mean vertex distance (all
	 * fronts share a fixed vertex count and orientation) so the renderer can
	 * morph them between timesteps. Same greedy assignment approach as the
	 * pressure center tracks.
	 */
	private correlateFronts(frames: SynopticDataFrame[]): void {
		let counter = 0
		let prev: SynopticDataFrame["fronts"] = []

		for (const frame of frames) {
			const current = frame.fronts

			type Candidate = { pi: number, ci: number, cost: number }
			const candidates: Candidate[] = []
			for (let pi = 0; pi < prev.length; pi++) {
				for (let ci = 0; ci < current.length; ci++) {
					let cost = meanFrontDistanceKm(prev[pi].points, current[ci].points)
					// Type changes (e.g. cold -> stationary) happen, but prefer
					// same-type continuations
					if (prev[pi].type !== current[ci].type) cost += 300
					if (cost < 600) candidates.push({ pi, ci, cost })
				}
			}

			candidates.sort((a, b) => a.cost - b.cost)
			const usedP = new Set<number>()
			const usedC = new Set<number>()
			for (const c of candidates) {
				if (usedP.has(c.pi) || usedC.has(c.ci)) continue
				usedP.add(c.pi)
				usedC.add(c.ci)
				current[c.ci].trackId = prev[c.pi].trackId
			}

			for (const front of current) {
				if (!front.trackId) front.trackId = `F-${++counter}`
			}

			prev = current
		}
	}

	/**
	 * Morph fronts between the current and next frame: matched tracks lerp
	 * vertex-by-vertex, unmatched fronts fade out as the transition
	 * progresses. New fronts appear when the frame flips.
	 */
	private interpolateFronts(): void {
		const currentFrame = this.synopticSeries.currentFrame
		const nextFrame = this.synopticSeries.nextFrame
		if (!currentFrame?.loaded) return

		const clockTime = this.context.clock.clockTime
		let t = 0
		if (nextFrame?.loaded) {
			const frameDiff = nextFrame.range.start.difference(currentFrame.range.start)
			if (frameDiff.jdelta > 0) {
				const sceneDiff = clockTime.difference(currentFrame.range.start)
				t = Math.max(0, Math.min(1, sceneDiff.jdelta / frameDiff.jdelta))
			}
		}

		if (t <= 0 || !nextFrame?.loaded) {
			for (const node of this.frontNodes.values()) {
				node.updateInterpolated(node.front.points, 1)
			}
			return
		}

		const nextByTrack = new Map<string, SynopticDataFrame["fronts"][number]>()
		for (const front of nextFrame.fronts) {
			if (front.trackId) nextByTrack.set(front.trackId, front)
		}

		for (const [key, node] of this.frontNodes) {
			const a = node.front
			const bPoints = nextByTrack.get(key)?.points ?? []
			if (bPoints.length !== a.points.length) {
				// No continuation: fade out through the transition
				node.updateInterpolated(a.points, 1 - t)
				continue
			}

			const points = a.points.map((pa, i) => {
				const pb = bPoints[i]
				return {
					lon: lerpLongitudeDeg(pa.lon, pb.lon, t),
					lat: pa.lat + (pb.lat - pa.lat) * t,
				}
			})
			node.updateInterpolated(points, 1)
		}
	}

	private updateIsobarLabels(labels: IsobarLabel[]): void {
		for (const node of this.isobarLabelNodes) {
			this.isobarLabelContainer.removeNode(node)
			node.destroy()
		}
		this.isobarLabelNodes = []

		for (const label of labels) {
			const node = new IsobarLabelNode(this.context, label, this.font!)
			this.isobarLabelNodes.push(node)
			this.isobarLabelContainer.addNode(node)
		}
	}

	private updatePressureCenters(centers: PressureCenterFeature[]): void {
		const context = this.context

		for (const node of this.pressureCenterNodes.values()) {
			this.pressureCenterContainer.removeNode(node)
			node.destroy()
		}
		this.pressureCenterNodes.clear()

		for (const center of centers) {
			const node = new PressureCenterNode(context, center, this.font!)
			const key = center.track_id || center.id
			this.pressureCenterNodes.set(key, node)
			this.pressureCenterContainer.addNode(node)
		}
	}

	public getAvailableTimes(): string[] {
		return this.synopticSeries.range.start.date.toISOString()
			? [this.synopticSeries.range.start.date.toISOString()]
			: []
	}

	public getCurrentTime(): string {
		return this.synopticSeries.currentFrame?.url ?? ""
	}

	override destroy(): void {
		if (SynopticDatasetLayer.live === this) SynopticDatasetLayer.live = undefined
		if (this.unsubscribe) {
			this.unsubscribe()
		}

		for (const node of this.pressureCenterNodes.values()) {
			node.destroy()
		}

		for (const node of this.trackNodes.values()) {
			node.destroy()
		}
		this.trackNodes.clear()

		for (const node of this.frontNodes.values()) {
			node.destroy()
		}
		this.frontNodes.clear()

		for (const node of this.isobarLabelNodes) {
			node.destroy()
		}
		this.isobarLabelNodes = []

		for (const node of this.heightLabelNodes) {
			node.destroy()
		}
		this.heightLabelNodes = []

		for (const node of this.jetLabelNodes) {
			node.destroy()
		}
		this.jetLabelNodes = []

		for (const node of this.axisNodes) {
			node.destroy()
		}
		this.axisNodes = []
		for (const frame of this.heightFrameCache.values()) {
			frame.purge()
		}
		this.heightFrameCache.clear()
		this.heightIsolineSphere.destroy()

		for (const frame of this.pressureFrameCache.values()) {
			frame.purge()
		}
		this.pressureFrameCache.clear()
		this.pressureIsolineSphere.destroy()

		for (const frame of this.jetFrameCache.values()) {
			frame.purge()
		}
		this.jetFrameCache.clear()
		this.jetStreamSphere.destroy()

		this.releaseComputeWorker()

		super.destroy()
	}
}

interface TrackState {
	id: string
	lon: number
	lat: number
	pressure: number
	seq: number
	lastTime: number
}

/** Motion across the frames around the displayed one, and the extreme pressure on the track */
function trackSummary(
	centerType: string,
	past: PressureTrackPoint[],
	future: PressureTrackPoint[]
): PressureTrackSummary {
	const summary: PressureTrackSummary = {}
	const from = past.length >= 2 ? past[past.length - 2] : past[past.length - 1]
	const to = future[0] ?? past[past.length - 1]
	if (from && to && to.timeMs > from.timeMs) {
		const km = haversineKm(from.lon, from.lat, to.lon, to.lat)
		const hours = (to.timeMs - from.timeMs) / 3600000
		summary.motion = { speedKmh: km / hours, bearingDeg: initialBearingDeg(from.lon, from.lat, to.lon, to.lat) }
	}
	const all = [...past, ...future]
	if (all.length > 0) {
		const pick = centerType === 'L'
			? all.reduce((a, b) => (b.pressureHpa < a.pressureHpa ? b : a))
			: all.reduce((a, b) => (b.pressureHpa > a.pressureHpa ? b : a))
		summary.extreme = { pressureHpa: pick.pressureHpa, timeMs: pick.timeMs }
	}
	return summary
}

function initialBearingDeg(lon1: number, lat1: number, lon2: number, lat2: number): number {
	const toRad = Math.PI / 180
	const phi1 = lat1 * toRad
	const phi2 = lat2 * toRad
	const dLon = (lon2 - lon1) * toRad
	const y = Math.sin(dLon) * Math.cos(phi2)
	const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon)
	return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360
}

/** Pressure change normalized to a 3 h interval; NaN when either side is missing */
function tendency3h(fromHpa: number | undefined, toHpa: number, hours: number): number {
	if (fromHpa === undefined || !(hours > 0)) return NaN
	return (toHpa - fromHpa) * 3 / hours
}

function lerpLongitudeDeg(aLon: number, bLon: number, t: number): number {
	let b = bLon
	const d = bLon - aLon
	if (d > 180) b -= 360
	else if (d < -180) b += 360
	const out = aLon + (b - aLon) * t
	if (out > 180) return out - 360
	if (out < -180) return out + 360
	return out
}

/** Mean haversine distance between corresponding vertices, sampled sparsely */
function meanFrontDistanceKm(
	a: { lon: number, lat: number }[],
	b: { lon: number, lat: number }[]
): number {
	const step = 4
	let sum = 0
	let count = 0
	const n = Math.min(a.length, b.length)
	for (let i = 0; i < n; i += step) {
		sum += haversineKm(a[i].lon, a[i].lat, b[i].lon, b[i].lat)
		count++
	}
	return count > 0 ? sum / count : Infinity
}

function haversineKm(lon1: number, lat1: number, lon2: number, lat2: number): number {
	const toRad = (deg: number) => deg * Math.PI / 180
	const r = 6371.0
	const phi1 = toRad(lat1)
	const phi2 = toRad(lat2)
	const dPhi = phi2 - phi1
	let dLon = lon2 - lon1
	if (dLon > 180) dLon -= 360
	if (dLon < -180) dLon += 360
	const dLonRad = toRad(dLon)
	const s1 = Math.sin(dPhi / 2)
	const s2 = Math.sin(dLonRad / 2)
	const a = s1 * s1 + Math.cos(phi1) * Math.cos(phi2) * s2 * s2
	return r * 2 * Math.asin(Math.min(1, Math.sqrt(a)))
}
